import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import {
  getMustChangeLoginStudentCount,
  getPendingStudentCount,
} from '../../modules/Student/services/studentService';
import { getOwnSectionGroups, getOwnSubjects } from '../../modules/Subject/services/subjectService';
import {
  ATTENTION_REVIEWED_EVENT,
  applyReviews,
  getAttentionList,
  getAttentionReviews,
  unaddressed,
} from '../../modules/Dashboard/services/attentionService';
import { getMustChangeLoginTeachers, getOwnAdvisory } from '../../modules/Teacher/services/teacherService';
import { useAuth } from '../../core/providers/AuthProvider';
import { ROLES } from '../constant/roles';

const REFRESH_MS = 30000;
// The attention list reads every grade and attendance total in the teacher's subjects, so it's
// refreshed less often than the counts above: on load, every few minutes, and when the bell opens.
const ATTENTION_REFRESH_MS = 5 * 60 * 1000;
// Names shown per notification before "and N more".
const MAX_NAMES = 3;

// The sections a teacher teaches plus their advisory section, as [{ grade_level, section }]: the
// same students their Students page lists.
async function getOwnGroups() {
  const [taught, advisory] = await Promise.all([getOwnSectionGroups(), getOwnAdvisory()]);
  const groups = new Map(taught.map((g) => [`${g.grade_level}|${g.section}`, g]));
  if (advisory) groups.set(`${advisory.grade_level}|${advisory.section}`, advisory);
  return [...groups.values()];
}

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

function NotificationItem({ to, onClick, children }) {
  return (
    <Link to={to} onClick={onClick} className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50">
      {children}
    </Link>
  );
}

const nameList = (students) => {
  const names = students.slice(0, MAX_NAMES).map((s) => `${s.firstname} ${s.lastname}`).join(', ');
  return students.length > MAX_NAMES ? `${names} and ${students.length - MAX_NAMES} more` : names;
};

// Counts students waiting for super admin approval, and accounts still on the username and password
// they were given (must_change_login), linking to the Students page filtered to them. Super admins
// see every pending student (they approve them) and every such student and teacher; teachers see
// the students in their own sections, so they know who isn't on their rosters yet or hasn't signed
// in to set up their login. Polled rather than pushed: students aren't in the realtime publication,
// so their rows are never broadcast (see "realtime" in schema.sql).
//
// Teachers also see the students in their subjects with a failing grade or frequent absences: the
// same list as the dashboard's "Students Needing Attention" section (attentionService), which the
// notifications link to. Students marked as addressed there are left out until things get worse.
function NotificationBell() {
  const { role } = useAuth();
  const isTeacher = role === ROLES.TEACHER;
  const [counts, setCounts] = useState({ pending: 0, students: 0, teachers: { count: 0, names: [] } });
  // Teachers only: [{ student }] lists of students failing a subject or often absent.
  const [attention, setAttention] = useState({ failing: [], absenteeism: [] });
  const count =
    counts.pending + counts.students + counts.teachers.count + attention.failing.length + attention.absenteeism.length;
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  // A teacher's sections, loaded once (null until then).
  const groupsRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      if (isTeacher && !groupsRef.current) groupsRef.current = await getOwnGroups();
      const groups = isTeacher ? groupsRef.current : undefined;
      const [pending, students, teachers] = await Promise.all([
        getPendingStudentCount(groups),
        getMustChangeLoginStudentCount(groups),
        isTeacher ? { count: 0, names: [] } : getMustChangeLoginTeachers(),
      ]);
      setCounts({ pending, students, teachers });
    } catch {
      // Keep the last known count; the next refresh will try again.
    }
  }, [isTeacher]);

  const refreshAttention = useCallback(async () => {
    if (!isTeacher) return;
    try {
      const [list, reviews] = await Promise.all([getOwnSubjects().then(getAttentionList), getAttentionReviews()]);
      setAttention(unaddressed(applyReviews(list, reviews)));
    } catch {
      // Keep the last known list; the next refresh will try again.
    }
  }, [isTeacher]);

  useEffect(() => {
    refreshAttention();
    const timer = setInterval(refreshAttention, ATTENTION_REFRESH_MS);
    window.addEventListener(ATTENTION_REVIEWED_EVENT, refreshAttention);
    return () => {
      clearInterval(timer);
      window.removeEventListener(ATTENTION_REVIEWED_EVENT, refreshAttention);
    };
  }, [refreshAttention]);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, REFRESH_MS);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, [refresh]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  const handleToggle = () => {
    if (!open) {
      refresh();
      refreshAttention();
    }
    setOpen((value) => !value);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleToggle}
        aria-label={count > 0 ? `Notifications, ${count} new` : 'Notifications'}
        aria-expanded={open}
        className="relative p-1 text-white/80 hover:text-white"
      >
        <BellIcon className="h-8 w-8" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-5 rounded-full bg-red-500 px-1 text-center text-xs font-semibold leading-5 text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-2 w-72 rounded-lg border border-gray-200 bg-white shadow-lg">
          <p className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-[#0b4a71]">Notifications</p>
          {count === 0 ? (
            <p className="px-4 py-4 text-sm text-gray-500">
              {isTeacher ? 'Nothing new for your sections.' : 'Nothing new.'}
            </p>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto divide-y divide-gray-100">
              {attention.failing.length > 0 && (
                // Both open the dashboard's attention section, where they can be marked as addressed.
                <NotificationItem to="/dashboard" onClick={() => setOpen(false)}>
                  <span className="font-semibold text-red-600">{plural(attention.failing.length, 'student')}</span>{' '}
                  with a failing grade in your subjects:{' '}
                  {nameList(attention.failing.map((e) => e.student))}.
                </NotificationItem>
              )}
              {attention.absenteeism.length > 0 && (
                <NotificationItem to="/dashboard" onClick={() => setOpen(false)}>
                  <span className="font-semibold text-amber-600">{plural(attention.absenteeism.length, 'student')}</span>{' '}
                  with frequent absences in your subjects:{' '}
                  {nameList(attention.absenteeism.map((e) => e.student))}.
                </NotificationItem>
              )}
              {counts.pending > 0 && (
                <Link
                  to="/dashboard/students"
                  // Lets the Students page open (or switch to) the pending list.
                  state={{ fromNotification: 'pending' }}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <span className="font-semibold text-[#0b4a71]">{plural(counts.pending, 'new student')}</span>{' '}
                  {isTeacher
                    ? "in your sections waiting for the administrator's approval. They won't appear in your subject grades until then."
                    : 'added by teachers waiting for approval'}
                </Link>
              )}
              {counts.students > 0 && (
                <Link
                  to="/dashboard/students"
                  state={{ fromNotification: 'must_change' }}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <span className="font-semibold text-[#0b4a71]">{plural(counts.students, 'student')}</span>{' '}
                  {isTeacher && 'in your sections '}still using the username and password they were given.
                  They'll be asked to change them at their next sign-in.
                </Link>
              )}
              {counts.teachers.count > 0 && (
                <Link
                  to="/dashboard/teachers"
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <span className="font-semibold text-[#0b4a71]">{plural(counts.teachers.count, 'teacher')}</span>{' '}
                  still using the username and password they were given:{' '}
                  {counts.teachers.names.join(', ')}
                  {counts.teachers.count > counts.teachers.names.length &&
                    ` and ${counts.teachers.count - counts.teachers.names.length} more`}
                  .
                </Link>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
